import React, { useEffect, useState } from 'react';
import { XIcon, PlayIcon, DatabaseIcon, CheckCircleIcon, UploadIcon, DownloadIcon, CodeIcon } from 'lucide-react';
import { Button } from '../Button';
interface DataEnrichmentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: any, isEditing: boolean) => void;
  endpoint?: any; // For editing existing endpoints
  onSubmitForApproval?: (id: number) => void;
}
export const DataEnrichmentFormModal: React.FC<DataEnrichmentFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  endpoint,
  onSubmitForApproval
}) => {
  const isEditing = !!endpoint;
  const [currentStep, setCurrentStep] = useState<'config' | 'preview' | 'summary'>('config');
  const [configurationType, setConfigurationType] = useState<'pull' | 'push'>(endpoint?.configurationType === 'push' ? 'push' : 'pull');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    configurationType: 'pull',
    // Pull configuration fields
    sourceType: 'sftp',
    host: '',
    port: '',
    username: '',
    password: '',
    authType: 'password',
    privateKey: '',
    headers: '',
    pathPattern: '',
    fileFormat: 'csv',
    delimiter: ',',
    hasHeaderRow: true,
    encoding: 'utf8',
    // Push configuration fields
    endpointPath: '',
    schemaDefinition: '',
    ingestMode: 'append',
    // Common fields
    targetSchema: '',
    targetTable: '',
    targetCollection: '',
    tenantId: ''
  });
  const [previewData, setPreviewData] = useState<any>({
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    previewRows: [],
    validationErrors: []
  });
  // Initialize form data when endpoint changes
  useEffect(() => {
    if (endpoint) {
      setConfigurationType(endpoint.configurationType === 'push' ? 'push' : 'pull');
      setFormData({
        name: endpoint.name || '',
        description: endpoint.description || '',
        configurationType: endpoint.configurationType || 'pull',
        sourceType: endpoint.sourceType || 'sftp',
        host: endpoint.host || '',
        port: endpoint.port || '',
        username: endpoint.username || '',
        password: endpoint.password || '',
        authType: endpoint.authType || 'password',
        privateKey: endpoint.privateKey || '',
        headers: endpoint.headers || '',
        pathPattern: endpoint.pathPattern || '',
        fileFormat: endpoint.fileFormat || 'csv',
        delimiter: endpoint.delimiter || ',',
        hasHeaderRow: endpoint.hasHeaderRow !== undefined ? endpoint.hasHeaderRow : true,
        encoding: endpoint.encoding || 'utf8',
        endpointPath: endpoint.endpointPath || '',
        schemaDefinition: endpoint.schemaDefinition || '',
        ingestMode: endpoint.ingestMode || 'append',
        targetSchema: endpoint.targetSchema || '',
        targetTable: endpoint.targetTable || '',
        targetCollection: endpoint.targetCollection || '',
        tenantId: endpoint.tenantId || ''
      });
    } else {
      // Reset form for new endpoint
      setConfigurationType('pull');
      setFormData({
        name: '',
        description: '',
        configurationType: 'pull',
        sourceType: 'sftp',
        host: '',
        port: '',
        username: '',
        password: '',
        authType: 'password',
        privateKey: '',
        headers: '',
        pathPattern: '',
        fileFormat: 'csv',
        delimiter: ',',
        hasHeaderRow: true,
        encoding: 'utf8',
        endpointPath: '',
        schemaDefinition: '',
        ingestMode: 'append',
        targetSchema: '',
        targetTable: '',
        targetCollection: '',
        tenantId: ''
      });
    }
  }, [endpoint]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'configurationType') {
      setConfigurationType(value as 'pull' | 'push');
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleTestRun = () => {
    // In a real application, this would make an API call to test the connection and parse a sample file
    // For now, we'll simulate a successful test with mock data
    const mockPreviewData = {
      totalRows: 156,
      validRows: 148,
      invalidRows: 8,
      previewRows: [{
        id: '001',
        name: 'John Smith',
        email: 'john@example.com',
        status: 'active'
      }, {
        id: '002',
        name: 'Jane Doe',
        email: 'jane@example.com',
        status: 'active'
      }, {
        id: '003',
        name: 'Bob Johnson',
        email: 'bob@example.com',
        status: 'inactive'
      }],
      validationErrors: [{
        row: 45,
        field: 'email',
        error: 'Invalid email format'
      }, {
        row: 67,
        field: 'status',
        error: 'Value not in allowed list'
      }, {
        row: 89,
        field: 'id',
        error: 'Duplicate ID'
      }]
    };
    setPreviewData(mockPreviewData);
    setCurrentStep('preview');
  };
  const handleSave = () => {
    onSave(formData, isEditing);
  };
  const handleSubmitForApproval = () => {
    if (isEditing && endpoint && onSubmitForApproval) {
      onSubmitForApproval(endpoint.id);
      onClose();
    }
  };
  const renderConfigTypeSelector = () => <div className="mb-6" data-id="element-867">
      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-868">
        Configuration Type
      </label>
      <div className="flex space-x-4" data-id="element-869">
        <label className="inline-flex items-center" data-id="element-870">
          <input type="radio" name="configurationType" value="pull" checked={configurationType === 'pull'} onChange={handleInputChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-871" />
          <span className="ml-2 flex items-center" data-id="element-872">
            <DownloadIcon size={16} className="mr-1 text-blue-500" data-id="element-873" />
            Pull (SFTP/HTTP)
          </span>
        </label>
        <label className="inline-flex items-center" data-id="element-874">
          <input type="radio" name="configurationType" value="push" checked={configurationType === 'push'} onChange={handleInputChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-875" />
          <span className="ml-2 flex items-center" data-id="element-876">
            <UploadIcon size={16} className="mr-1 text-purple-500" data-id="element-877" />
            Push (REST API)
          </span>
        </label>
      </div>
      <p className="mt-1 text-sm text-gray-500" data-id="element-878">
        {configurationType === 'pull' ? 'Pull configuration allows you to fetch data from external sources like SFTP or HTTP endpoints.' : 'Push configuration creates a REST API endpoint where external systems can send data to your system.'}
      </p>
    </div>;
  const renderPullConfigForm = () => <div className="space-y-6" data-id="element-879">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-880">
        <div data-id="element-881">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-882">
            Endpoint Name
          </label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter endpoint name" required data-id="element-883" />
        </div>
        <div data-id="element-884">
          <label htmlFor="sourceType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-885">
            Source Type
          </label>
          <select id="sourceType" name="sourceType" value={formData.sourceType} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-886">
            <option value="sftp" data-id="element-887">SFTP</option>
            <option value="http" data-id="element-888">HTTP</option>
          </select>
        </div>
      </div>
      <div data-id="element-889">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-890">
          Description
        </label>
        <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter endpoint description" data-id="element-891" />
      </div>
      <div className="bg-blue-50 p-4 rounded-md" data-id="element-892">
        <h3 className="text-md font-medium text-blue-900 mb-3" data-id="element-893">
          Connection Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-894">
          <div data-id="element-895">
            <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-896">
              {formData.sourceType === 'sftp' ? 'Host' : 'URL'}
            </label>
            <input type="text" id="host" name="host" value={formData.host} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={formData.sourceType === 'sftp' ? 'sftp.example.com' : 'https://api.example.com/data'} required data-id="element-897" />
          </div>
          {formData.sourceType === 'sftp' && <div data-id="element-898">
              <label htmlFor="port" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-899">
                Port
              </label>
              <input type="text" id="port" name="port" value={formData.port} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="22" data-id="element-900" />
            </div>}
          {formData.sourceType === 'sftp' && <>
              <div data-id="element-901">
                <label htmlFor="authType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-902">
                  Authentication Type
                </label>
                <select id="authType" name="authType" value={formData.authType} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-903">
                  <option value="password" data-id="element-904">Username & Password</option>
                  <option value="key" data-id="element-905">Private Key</option>
                </select>
              </div>
              <div data-id="element-906">
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-907">
                  Username
                </label>
                <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter username" required data-id="element-908" />
              </div>
              {formData.authType === 'password' ? <div data-id="element-909">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-910">
                    Password
                  </label>
                  <input type="password" id="password" name="password" value={formData.password} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter password" data-id="element-911" />
                </div> : <div className="md:col-span-2" data-id="element-912">
                  <label htmlFor="privateKey" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-913">
                    Private Key
                  </label>
                  <textarea id="privateKey" name="privateKey" value={formData.privateKey} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter private key" data-id="element-914" />
                </div>}
            </>}
          {formData.sourceType === 'http' && <div className="md:col-span-2" data-id="element-915">
              <label htmlFor="headers" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-916">
                Headers (JSON format)
              </label>
              <textarea id="headers" name="headers" value={formData.headers} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}' data-id="element-917" />
            </div>}
        </div>
      </div>
      <div className="bg-green-50 p-4 rounded-md" data-id="element-918">
        <h3 className="text-md font-medium text-green-900 mb-3" data-id="element-919">
          File Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-920">
          <div data-id="element-921">
            <label htmlFor="pathPattern" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-922">
              {formData.sourceType === 'sftp' ? 'Path/Pattern' : 'Endpoint Path'}
            </label>
            <input type="text" id="pathPattern" name="pathPattern" value={formData.pathPattern} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={formData.sourceType === 'sftp' ? '/inbound/*.csv' : '/api/data'} required data-id="element-923" />
          </div>
          <div data-id="element-924">
            <label htmlFor="fileFormat" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-925">
              File Format
            </label>
            <select id="fileFormat" name="fileFormat" value={formData.fileFormat} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-926">
              <option value="csv" data-id="element-927">CSV</option>
              <option value="tsv" data-id="element-928">TSV</option>
              <option value="json" data-id="element-929">JSON</option>
            </select>
          </div>
          {(formData.fileFormat === 'csv' || formData.fileFormat === 'tsv') && <>
              <div data-id="element-930">
                <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-931">
                  Delimiter
                </label>
                <input type="text" id="delimiter" name="delimiter" value={formData.delimiter} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={formData.fileFormat === 'csv' ? ',' : '\t'} maxLength={1} data-id="element-932" />
              </div>
              <div className="flex items-center h-full pt-6" data-id="element-933">
                <input type="checkbox" id="hasHeaderRow" name="hasHeaderRow" checked={formData.hasHeaderRow} onChange={handleInputChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-934" />
                <label htmlFor="hasHeaderRow" className="ml-2 block text-sm text-gray-700" data-id="element-935">
                  Has header row
                </label>
              </div>
              <div data-id="element-936">
                <label htmlFor="encoding" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-937">
                  Encoding
                </label>
                <select id="encoding" name="encoding" value={formData.encoding} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-938">
                  <option value="utf8" data-id="element-939">UTF-8</option>
                  <option value="ascii" data-id="element-940">ASCII</option>
                  <option value="latin1" data-id="element-941">Latin-1</option>
                  <option value="utf16le" data-id="element-942">UTF-16</option>
                </select>
              </div>
            </>}
        </div>
      </div>
      <div className="bg-purple-50 p-4 rounded-md" data-id="element-943">
        <h3 className="text-md font-medium text-purple-900 mb-3" data-id="element-944">
          Target PostgreSQL Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-945">
          <div data-id="element-946">
            <label htmlFor="targetSchema" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-947">
              Schema
            </label>
            <input type="text" id="targetSchema" name="targetSchema" value={formData.targetSchema} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="public" required data-id="element-948" />
          </div>
          <div data-id="element-949">
            <label htmlFor="targetTable" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-950">
              Table
            </label>
            <input type="text" id="targetTable" name="targetTable" value={formData.targetTable} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customers" required data-id="element-951" />
          </div>
        </div>
      </div>
    </div>;
  const renderPushConfigForm = () => <div className="space-y-6" data-id="element-952">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-953">
        <div data-id="element-954">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-955">
            Endpoint Name
          </label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter endpoint name" required data-id="element-956" />
        </div>
        <div data-id="element-957">
          <label htmlFor="endpointPath" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-958">
            API Path Pattern
          </label>
          <div className="flex items-center" data-id="element-959">
            <span className="text-gray-500 mr-1" data-id="element-960">/v1/enrich/</span>
            <input type="text" id="endpointPath" name="endpointPath" value={formData.endpointPath} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customers/data" required data-id="element-961" />
          </div>
        </div>
      </div>
      <div data-id="element-962">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-963">
          Description
        </label>
        <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter endpoint description" data-id="element-964" />
      </div>
      <div className="bg-green-50 p-4 rounded-md" data-id="element-965">
        <h3 className="text-md font-medium text-green-900 mb-3" data-id="element-966">
          Ingest Settings
        </h3>
        <div className="space-y-4" data-id="element-967">
          <div data-id="element-968">
            <label htmlFor="ingestMode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-969">
              Ingest Mode
            </label>
            <select id="ingestMode" name="ingestMode" value={formData.ingestMode} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-970">
              <option value="append" data-id="element-971">
                Append - Add new records to existing data
              </option>
              <option value="replace" data-id="element-972">
                Replace - Archive existing data and append new data
              </option>
            </select>
            <p className="mt-1 text-xs text-gray-500" data-id="element-973">
              {formData.ingestMode === 'append' ? 'Append mode adds new records to the existing dataset.' : 'Replace mode archives the current dataset and creates a new version with the uploaded data.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-974">
            <div data-id="element-975">
              <label htmlFor="targetCollection" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-976">
                Target Collection
              </label>
              <input type="text" id="targetCollection" name="targetCollection" value={formData.targetCollection} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customers" required data-id="element-977" />
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderConfigStep = () => <div className="space-y-6" data-id="element-978">
      {renderConfigTypeSelector()}
      {configurationType === 'pull' ? renderPullConfigForm() : renderPushConfigForm()}
      <div className="flex justify-end space-x-4" data-id="element-979">
        <Button variant="secondary" onClick={onClose} data-id="element-980">
          Cancel
        </Button>
        {isEditing && endpoint?.status === 'In-Progress' && <Button variant="secondary" onClick={handleSubmitForApproval} data-id="element-981">
            Submit for Approval
          </Button>}
        <Button variant="primary" icon={<PlayIcon size={16} data-id="element-983" />} onClick={handleTestRun} data-id="element-982">
          Test Run
        </Button>
      </div>
    </div>;
  const renderPreviewStep = () => <div className="space-y-6" data-id="element-984">
      <div className="bg-green-50 p-4 rounded-md" data-id="element-985">
        <h3 className="text-md font-medium text-green-900 mb-3" data-id="element-986">
          Test Run Results
        </h3>
        <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-987">
          <div className="bg-white p-3 rounded-md shadow-sm" data-id="element-988">
            <p className="text-sm text-gray-500" data-id="element-989">Total Rows</p>
            <p className="text-xl font-semibold" data-id="element-990">{previewData.totalRows}</p>
          </div>
          <div className="bg-white p-3 rounded-md shadow-sm" data-id="element-991">
            <p className="text-sm text-gray-500" data-id="element-992">Valid Rows</p>
            <p className="text-xl font-semibold text-green-600" data-id="element-993">
              {previewData.validRows}
            </p>
          </div>
          <div className="bg-white p-3 rounded-md shadow-sm" data-id="element-994">
            <p className="text-sm text-gray-500" data-id="element-995">Invalid Rows</p>
            <p className="text-xl font-semibold text-red-600" data-id="element-996">
              {previewData.invalidRows}
            </p>
          </div>
        </div>
      </div>
      {previewData.validationErrors.length > 0 && <div className="bg-red-50 p-4 rounded-md" data-id="element-997">
          <h3 className="text-md font-medium text-red-900 mb-3" data-id="element-998">
            Validation Errors
          </h3>
          <div className="max-h-40 overflow-y-auto" data-id="element-999">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1000">
              <thead className="bg-gray-50" data-id="element-1001">
                <tr data-id="element-1002">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1003">
                    Row
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1004">
                    Field
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1005">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1006">
                {previewData.validationErrors.map((error, index) => <tr key={index} data-id="element-1007">
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1008">
                      {error.row}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1009">
                      {error.field}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-red-500" data-id="element-1010">
                      {error.error}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      <div className="bg-white p-4 rounded-md shadow border border-gray-200" data-id="element-1011">
        <h3 className="text-md font-medium text-gray-900 mb-3" data-id="element-1012">Data Preview</h3>
        <div className="overflow-x-auto" data-id="element-1013">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1014">
            <thead className="bg-gray-50" data-id="element-1015">
              <tr data-id="element-1016">
                {previewData.previewRows.length > 0 && Object.keys(previewData.previewRows[0]).map(header => <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1017">
                      {header}
                    </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1018">
              {previewData.previewRows.map((row, rowIndex) => <tr key={rowIndex} data-id="element-1019">
                  {Object.values(row).map((value, colIndex) => <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1020">
                      {String(value)}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      {configurationType === 'push' && <div className="bg-blue-50 p-4 rounded-md" data-id="element-1021">
          <h3 className="text-md font-medium text-blue-900 mb-3" data-id="element-1022">
            API Response Example
          </h3>
          <pre className="bg-white p-3 rounded border border-gray-200 text-sm font-mono overflow-x-auto" data-id="element-1023">
            {`{
  "success": true,
  "correlationId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "stats": {
    "accepted": ${previewData.validRows},
    "rejected": ${previewData.invalidRows},
    "total": ${previewData.totalRows}
  },
  "timestamp": "${new Date().toISOString()}"
}`}
          </pre>
        </div>}
      <div className="flex justify-end space-x-4" data-id="element-1024">
        <Button variant="secondary" onClick={() => setCurrentStep('config')} data-id="element-1025">
          Back to Configuration
        </Button>
        <Button variant="primary" icon={<DatabaseIcon size={16} data-id="element-1027" />} onClick={() => setCurrentStep('summary')} data-id="element-1026">
          Continue
        </Button>
      </div>
    </div>;
  const renderSummaryStep = () => <div className="space-y-6" data-id="element-1028">
      <div className="flex items-center justify-center py-4" data-id="element-1029">
        <div className="bg-green-100 rounded-full p-3" data-id="element-1030">
          <CheckCircleIcon size={32} className="text-green-600" data-id="element-1031" />
        </div>
      </div>
      <div className="text-center" data-id="element-1032">
        <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1033">
          {isEditing ? 'Ready to Update Endpoint' : 'Ready to Create Endpoint'}
        </h3>
        <p className="text-gray-500" data-id="element-1034">
          Your data enrichment endpoint has been validated and is ready to be
          {isEditing ? ' updated.' : ' created.'}
        </p>
      </div>
      <div className="bg-gray-50 p-4 rounded-md" data-id="element-1035">
        <h3 className="text-md font-medium text-gray-900 mb-3" data-id="element-1036">
          Endpoint Summary
        </h3>
        <div className="space-y-3" data-id="element-1037">
          <div className="grid grid-cols-3 gap-4" data-id="element-1038">
            <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1039">
              Type:
            </div>
            <div className="col-span-2 text-sm text-gray-900 flex items-center" data-id="element-1040">
              {configurationType === 'pull' ? <>
                  <DownloadIcon size={16} className="mr-1 text-blue-500" data-id="element-1041" />
                  Pull Configuration (SFTP/HTTP)
                </> : <>
                  <UploadIcon size={16} className="mr-1 text-purple-500" data-id="element-1042" />
                  Push Configuration (REST API)
                </>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4" data-id="element-1043">
            <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1044">
              Name:
            </div>
            <div className="col-span-2 text-sm text-gray-900" data-id="element-1045">
              {formData.name}
            </div>
          </div>
          {configurationType === 'pull' ? <>
              <div className="grid grid-cols-3 gap-4" data-id="element-1046">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1047">
                  Source Type:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1048">
                  {formData.sourceType.toUpperCase()}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-1049">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1050">
                  Connection:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1051">
                  {formData.sourceType === 'sftp' ? `${formData.host}:${formData.port || '22'}` : formData.host}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-1052">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1053">
                  Path/Pattern:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1054">
                  {formData.pathPattern}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-1055">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1056">
                  File Format:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1057">
                  {formData.fileFormat.toUpperCase()}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-1058">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1059">
                  Target:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1060">{`${formData.targetSchema}.${formData.targetTable}`}</div>
              </div>
            </> : <>
              <div className="grid grid-cols-3 gap-4" data-id="element-1061">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1062">
                  API Endpoint:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1063">{`/v1/enrich/${formData.endpointPath}`}</div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-1064">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1065">
                  Ingest Mode:
                </div>
                <div className="col-span-2 text-sm text-gray-900 capitalize" data-id="element-1066">
                  {formData.ingestMode}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-1067">
                <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1068">
                  Target Collection:
                </div>
                <div className="col-span-2 text-sm text-gray-900" data-id="element-1069">
                  {formData.targetCollection}
                </div>
              </div>
            </>}
          <div className="grid grid-cols-3 gap-4" data-id="element-1070">
            <div className="col-span-1 text-sm font-medium text-gray-500" data-id="element-1071">
              Validation:
            </div>
            <div className="col-span-2 text-sm text-gray-900" data-id="element-1072">
              {previewData.invalidRows === 0 ? <span className="text-green-600" data-id="element-1073">All rows valid</span> : <span className="text-yellow-600" data-id="element-1074">
                  {previewData.invalidRows} invalid rows
                </span>}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-blue-50 p-4 rounded-md" data-id="element-1075">
        <p className="text-sm text-blue-700" data-id="element-1076">
          <strong data-id="element-1077">Note:</strong>{' '}
          {isEditing ? 'This update will be version controlled and all changes will be audit-logged.' : 'This endpoint will be created with version 1.0 and all changes will be audit-logged.'}
        </p>
      </div>
      <div className="flex justify-end space-x-4" data-id="element-1078">
        <Button variant="secondary" onClick={() => setCurrentStep('preview')} data-id="element-1079">
          Back
        </Button>
        {isEditing && endpoint?.status === 'In-Progress' && <Button variant="secondary" onClick={handleSubmitForApproval} data-id="element-1080">
            Submit for Approval
          </Button>}
        <Button variant="primary" onClick={handleSave} data-id="element-1081">
          {isEditing ? 'Update Endpoint' : 'Create Endpoint'}
        </Button>
      </div>
    </div>;
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-1082">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden" data-id="element-1083">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200" data-id="element-1084">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-1085">
            {isEditing ? `Edit Data Enrichment Endpoint: ${endpoint.name}` : 'Define New Data Enrichment Endpoint'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1086">
            <XIcon size={24} data-id="element-1087" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]" data-id="element-1088">
          {currentStep === 'config' && renderConfigStep()}
          {currentStep === 'preview' && renderPreviewStep()}
          {currentStep === 'summary' && renderSummaryStep()}
        </div>
      </div>
    </div>;
};